import { useEffect, useState, useMemo } from "react";
import { Gift, Handshake, IndianRupee, TrendingUp, Users, Clock, CheckCircle2, ChevronRight, ArrowLeft, LogOut, LayoutDashboard, ScrollText, Wallet, User as UserIcon, Sparkles, Store, MapPin, Navigation, MessageSquare, Map } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import useLocations from "@/hooks/useLocations";
import useBranches from "@/hooks/useBranches";
import { LoanIcon, findLoan, loanRangeLabel, clampAmount } from "@/lib/loanUtils";

const inrCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2).replace(/\.00$/, "")} L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `₹${v.toLocaleString("en-IN")}`;
};
const inrFull = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => { if(!iso) return "—"; try { return new Date(iso).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}); } catch { return "—"; } };
const fmtDateTime = (iso) => { if(!iso) return "—"; try { const d = new Date(iso); return `${d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})} · ${d.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}`; } catch { return "—"; } };

export default function PartnerDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [profile, setProfile] = useState(user);

  useEffect(() => {
    api.get("/partner/profile").then(r => setProfile(r.data)).catch(() => {});
  }, []);

  const isVerified = profile?.verification_status === "verified";

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#0F172A]/95 backdrop-blur border-b border-[#1E2742]">
        <div className="max-w-md mx-auto flex items-center gap-2 px-4 h-14">
          <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37]">
            <Handshake className="w-4 h-4"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]">Partner</div>
            <div className="font-bold text-sm truncate">{profile?.name || "—"}</div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:text-red-400 p-2"><LogOut className="w-4 h-4"/></button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-4">
        {!isVerified && (
          <div className="rounded-xl border-2 border-[#F97316]/40 bg-[#F97316]/10 p-3 mb-3 text-xs text-[#FDB979]" data-testid="partner-not-verified">
            <b>Verification pending.</b> You can browse but referrals unlock after admin approves your office.
          </div>
        )}

        {tab === "dashboard"  && <DashboardTab profile={profile} isVerified={isVerified}/>}
        {tab === "leads"      && <LeadsTab isVerified={isVerified}/>}
        {tab === "commission" && <CommissionTab/>}
        {tab === "profile"    && <ProfileTab profile={profile}/>}
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0F172A]/95 backdrop-blur border-t border-[#1E2742]">
        <div className="max-w-md mx-auto grid grid-cols-4">
          <BottomBtn active={tab === "dashboard"} onClick={()=>setTab("dashboard")} icon={LayoutDashboard} label="Home" testId="tab-dashboard"/>
          <BottomBtn active={tab === "leads"} onClick={()=>setTab("leads")} icon={ScrollText} label="My Leads" testId="tab-leads"/>
          <BottomBtn active={tab === "commission"} onClick={()=>setTab("commission")} icon={Wallet} label="Commission" testId="tab-commission"/>
          <BottomBtn active={tab === "profile"} onClick={()=>setTab("profile")} icon={UserIcon} label="Profile" testId="tab-profile"/>
        </div>
      </div>
    </div>
  );
}

function BottomBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`flex flex-col items-center py-2 gap-0.5 ${active ? "text-[#D4AF37]" : "text-slate-500"}`}>
      <Icon className="w-5 h-5"/>
      <div className="text-[10px] font-bold">{label}</div>
    </button>
  );
}

// ---------- Dashboard tab ----------
function DashboardTab({ profile, isVerified }) {
  const [stats, setStats] = useState({ total_referred: 0, total_loan_amount: 0, total_commission: 0, paid_commission: 0, pending_commission: 0 });
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/partner/refer/stats"),
        api.get("/partner/refer/list"),
      ]);
      setStats(s.data); setItems(l.data.items || []);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="partner-dashboard">
      <EarningsCard stats={stats}/>

      <div className="rounded-2xl border-2 border-[#D4AF37]/60 p-4 relative overflow-hidden" style={{background:"linear-gradient(90deg,#2b1f08,#3a2a0a,#2b1f08)"}} data-testid="partner-banner">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#D4AF37] text-slate-900 flex items-center justify-center shadow-lg"><Gift className="w-5 h-5"/></div>
          <div>
            <div className="text-[#D4AF37] font-heading font-bold text-sm">Refer a customer · Earn 1%</div>
            <div className="text-[11px] text-[#D4AF37]/70 mt-0.5">on Disbursed Loan Amount · Unlimited Earnings · No Cap</div>
          </div>
        </div>
        <Button data-testid="open-add-lead" disabled={!isVerified} onClick={()=>setAddOpen(true)} className="mt-3 w-full h-12 bg-[#F97316] hover:bg-[#ea6a10] text-white font-black rounded-full text-sm disabled:opacity-50">
          + Add New Customer Lead · Earn 1%
        </Button>
      </div>

      <RecentLeads items={items.slice(0,5)}/>

      <AddPartnerLeadDialog open={addOpen} onClose={()=>setAddOpen(false)} profile={profile} onDone={load}/>
    </div>
  );
}

function EarningsCard({ stats }) {
  return (
    <div className="rounded-2xl p-4 border-2 border-[#D4AF37]/60 relative overflow-hidden" style={{background:"linear-gradient(140deg,#1a1305,#2b1f08 45%,#3a2a0a)",boxShadow:"0 8px 32px -8px rgba(212,175,55,0.35), inset 0 1px 0 rgba(212,175,55,0.2)"}} data-testid="partner-earnings-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#8a6f1c] text-slate-900 flex items-center justify-center shadow-lg"><TrendingUp className="w-5 h-5"/></div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">My Partner</div>
          <div className="text-white font-heading text-lg">Earnings</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <StatBox label="Total Referred" value={String(stats.total_referred||0)} sub="Cases" tone="dim" testId="stat-total-referred"/>
        <StatBox label="Total Loan" value={inrCompact(stats.total_loan_amount||0)} sub="Amount" tone="dim" testId="stat-total-loan"/>
        <StatBox label="Total Commission" value={inrCompact(stats.total_commission||0)} sub="@ 1%" tone="gold" large testId="stat-total-commission"/>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <StatBox label="Pending" value={inrFull(stats.pending_commission||0)} sub="Awaiting Disbursal" tone="orange" testId="stat-pending"/>
        <StatBox label="Paid Commission" value={inrFull(stats.paid_commission||0)} sub="Credited to Account" tone="green" testId="stat-paid"/>
      </div>
      <div className="mt-3 text-center text-[10px] text-[#D4AF37]/80 font-mono-alt">₹5L = ₹5K · ₹10L = ₹10K · No Cap!</div>
      <div className="mt-1 text-center text-[9px] text-[#D4AF37]/60">You earn 1% · We charge 2% total (1% you, 1% us)</div>
    </div>
  );
}

function StatBox({ label, value, sub, tone="dim", large=false, testId }) {
  const bg = { gold:"bg-[#D4AF37]/15 border-[#D4AF37]/60", orange:"bg-[#F97316]/12 border-[#F97316]/50", green:"bg-[#22C55E]/12 border-[#22C55E]/50", dim:"bg-black/25 border-white/10" }[tone];
  const c = { gold:"text-[#F4CF52]", orange:"text-[#FDB979]", green:"text-[#4ADE80]", dim:"text-white" }[tone];
  return (
    <div data-testid={testId} className={`rounded-xl border p-2.5 ${bg}`}>
      <div className="text-[9px] uppercase tracking-widest text-white/60 font-bold leading-tight">{label}</div>
      <div className={`font-mono-alt font-black mt-1 leading-none ${c} ${large ? "text-xl" : "text-base"}`}>{value}</div>
      <div className="text-[9px] text-white/50 mt-1 leading-tight">{sub}</div>
    </div>
  );
}

function RecentLeads({ items }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2 mt-1 flex items-center gap-1.5"><Users className="w-3.5 h-3.5"/> Recent Referrals</div>
      <div className="space-y-2">{items.map(it => <LeadCard key={it.lead_id} it={it}/>)}</div>
    </div>
  );
}

// ---------- Leads tab ----------
function LeadsTab({ isVerified }) {
  const [view, setView] = useState("list"); // list | map
  const [mapData, setMapData] = useState(null);
  const [items, setItems] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const load = () => {
    setLoading(true);
    api.get("/partner/customers-map")
      .then(r => { setMapData(r.data); setItems(r.data.customers || []); })
      .catch(e => toast.error(errorMessage(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const requestLocation = async (lead) => {
    try {
      const { data } = await api.post(`/partner/leads/${lead.lead_id}/request-location`);
      window.open(data.wa_link, "_blank", "noopener");
      toast.success("WhatsApp opened — send to request location");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  if (detailId) return <LeadDetail leadId={detailId} onBack={()=>{setDetailId(null); load();}}/>;

  return (
    <div className="space-y-3" data-testid="partner-leads-tab">
      <Button data-testid="leads-add" disabled={!isVerified} onClick={()=>setAddOpen(true)} className="w-full h-12 bg-[#F97316] hover:bg-[#ea6a10] text-white font-black rounded-full text-sm disabled:opacity-50">
        + Add New Customer Lead · Earn 1%
      </Button>

      {/* View toggle */}
      <div className="grid grid-cols-2 rounded-full bg-slate-950 border border-white/10 p-1 text-xs">
        <button data-testid="view-list" onClick={()=>setView("list")} className={`h-9 rounded-full font-bold ${view==="list"?"bg-[#D4AF37] text-slate-900":"text-slate-400"}`}>List</button>
        <button data-testid="view-map" onClick={()=>setView("map")} className={`h-9 rounded-full font-bold flex items-center justify-center gap-1 ${view==="map"?"bg-[#D4AF37] text-slate-900":"text-slate-400"}`}><Map className="w-3 h-3"/> Map View</button>
      </div>

      {loading ? <div className="text-slate-500 text-center py-8">Loading…</div> :
       items.length === 0 ? (
        <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-8 text-center" data-testid="leads-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-3"><Gift className="w-6 h-6 text-[#D4AF37]"/></div>
          <div className="text-white font-bold">No leads yet</div>
          <div className="text-slate-500 text-xs mt-1">Tap +Add New Customer Lead to start.</div>
        </div>
       ) : view === "map" ? (
        <MapView office={mapData?.office} customers={items}/>
       ) : (
        <div className="space-y-2">
          {items.map(it => <LeadCard key={it.lead_id} it={it} onOpen={()=>setDetailId(it.lead_id)} onReqLoc={requestLocation}/>)}
        </div>
       )}

      <AddPartnerLeadDialog open={addOpen} onClose={()=>setAddOpen(false)} profile={user} onDone={load}/>
    </div>
  );
}

function MapView({ office, customers }) {
  const shared = customers.filter(c => c.location_shared);
  const centerLat = office?.lat;
  const centerLng = office?.lng;
  // Build multi-point Google Maps URL using markers query params (simple embed)
  const markers = shared.map(c => `${c.customer_lat},${c.customer_lng}`).join("|");
  const src = centerLat && centerLng
    ? `https://maps.google.com/maps?q=${centerLat},${centerLng}&z=10&output=embed`
    : null;
  return (
    <div className="space-y-2" data-testid="partner-map-view">
      {src ? (
        <div className="rounded-2xl overflow-hidden border border-white/10">
          <iframe title="Customers map" className="w-full h-72" src={src} loading="lazy"/>
        </div>
      ) : (
        <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-6 text-center text-slate-500 text-sm">Set your Office GPS during registration to see the map.</div>
      )}
      <div className="text-[10px] uppercase tracking-widest text-slate-500 px-1">
        {shared.length} of {customers.length} customer(s) have shared their location.
      </div>
      <div className="space-y-2">
        {customers.map(c => (
          <div key={c.lead_id} className="rounded-lg bg-[#141A2E] border border-[#1E2742] p-2 flex items-center gap-2" data-testid={`map-row-${c.lead_id}`}>
            <span className={`w-2 h-2 rounded-full ${c.location_shared ? "bg-emerald-400" : "bg-slate-600"}`}/>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm truncate">{c.name}</div>
              <div className="text-[10px] text-slate-500">
                {c.location_shared ? `📍 Shared${c.distance_km !== null ? ` · ${c.distance_km} km` : ""}` : "Not shared"}
              </div>
            </div>
            {c.location_shared && (
              <a data-testid={`map-open-${c.lead_id}`} target="_blank" rel="noreferrer" href={c.customer_gmap_link} className="text-cyan-400 text-xs hover:underline">Open</a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LeadCard({ it, onOpen, onReqLoc }) {
  const inits = (it.name||"?").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase();
  const shared = !!it.location_shared;
  return (
    <div data-testid={`lead-row-${it.lead_id}`} className="rounded-2xl bg-[#141A2E] border border-[#1E2742] hover:border-[#D4AF37]/40 p-3">
      <button type="button" onClick={onOpen} className="w-full text-left">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8a6f1c] flex items-center justify-center text-slate-900 font-black text-sm">{inits}</div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-bold text-sm truncate">{it.name}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span className="font-mono-alt">{inrFull(it.amount)}</span> · <span className={it.disbursed?"text-emerald-400 font-bold":"text-[#F97316]"}>{it.disbursed?"Disbursed":(it.status||"In Progress").replace(/_/g," ")}</span>
            </div>
            <div className="text-[10px] mt-1 flex items-center gap-1.5 flex-wrap">
              <span className={shared ? "text-emerald-400" : "text-slate-500"}>
                {shared ? `📍 Shared${it.distance_km!==null ? ` · ${it.distance_km} km` : ""}` : "📍 Not Shared"}
              </span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]/70">Commission</div>
            <div className="text-[#F4CF52] font-mono-alt font-black text-sm">{inrFull(it.commission_amount)}</div>
            <div className="mt-1"><CommBadge paid={it.commission_paid}/></div>
          </div>
          {onOpen && <ChevronRight className="w-4 h-4 text-slate-600"/>}
        </div>
      </button>
      {/* Location actions */}
      {onReqLoc && (
        <div className="mt-2 grid grid-cols-2 gap-2 pt-2 border-t border-white/5">
          {shared ? (
            <a data-testid={`view-cust-map-${it.lead_id}`} onClick={e=>e.stopPropagation()} target="_blank" rel="noreferrer" href={it.customer_gmap_link} className="h-9 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 text-xs flex items-center justify-center gap-1 font-bold"><MapPin className="w-3 h-3"/>View on Map</a>
          ) : (
            <button data-testid={`req-loc-${it.lead_id}`} type="button" onClick={(e)=>{e.stopPropagation(); onReqLoc(it);}} className="h-9 rounded-full bg-[#F97316]/10 border border-[#F97316]/30 text-[#FDB979] hover:bg-[#F97316]/20 text-xs flex items-center justify-center gap-1 font-bold"><MessageSquare className="w-3 h-3"/>Request Location</button>
          )}
          {shared ? (
            <a data-testid={`directions-${it.lead_id}`} onClick={e=>e.stopPropagation()} target="_blank" rel="noreferrer" href={`https://maps.google.com/?daddr=${it.customer_lat},${it.customer_lng}`} className="h-9 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 text-xs flex items-center justify-center gap-1 font-bold"><Navigation className="w-3 h-3"/>Get Directions</a>
          ) : (
            it.phone && <a onClick={e=>e.stopPropagation()} href={`tel:${it.phone}`} className="h-9 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 text-xs flex items-center justify-center gap-1 font-bold">Call Customer</a>
          )}
        </div>
      )}
    </div>
  );
}

function CommBadge({ paid, size="sm" }) {
  const cls = paid ? "bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/50" : "bg-[#F97316]/20 text-[#FDB979] border-[#F97316]/50";
  const dotCls = paid ? "bg-[#4ADE80]" : "bg-[#FDB979]";
  const s = size === "lg" ? "text-xs px-3 py-1.5" : "text-[10px] px-2 py-1";
  return (
    <span data-testid={paid?"badge-paid":"badge-pending"} className={`inline-flex items-center gap-1.5 rounded-full border font-bold uppercase tracking-widest ${cls} ${s}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls} ${paid?"":"animate-pulse"}`}/>
      {paid ? "Paid ✓" : "Pending ⏳"}
    </span>
  );
}

function LeadDetail({ leadId, onBack }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/partner/refer/${leadId}`).then(r=>setD(r.data)).catch(e=>{toast.error(errorMessage(e));onBack();}); }, [leadId]);
  if (!d) return <div className="text-slate-500 text-center py-12">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="partner-lead-detail">
      <button onClick={onBack} data-testid="detail-back" className="inline-flex items-center gap-1.5 text-slate-400 text-sm py-1"><ArrowLeft className="w-4 h-4"/> Back</button>
      <div className="rounded-2xl p-4 border-2 border-[#D4AF37]/50" style={{background:"linear-gradient(140deg,#1a1305,#2b1f08)"}}>
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">Referred Customer</div>
        <div className="font-heading text-2xl text-white mt-0.5">{d.name}</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-black/30 border border-white/10 p-2.5"><div className="text-[9px] uppercase text-white/50 font-bold">Reference ID</div><div className="text-[#D4AF37] font-mono-alt font-bold mt-0.5 text-xs">{d.lead_id}</div></div>
          <div className="rounded-lg bg-black/30 border border-white/10 p-2.5"><div className="text-[9px] uppercase text-white/50 font-bold">Loan Amount</div><div className="text-white font-mono-alt font-bold mt-0.5 text-sm">{inrFull(d.amount)}</div></div>
        </div>
        <div className="mt-2 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/40 p-2.5 flex items-center justify-between gap-2">
          <div><div className="text-[9px] uppercase text-[#D4AF37] font-bold">Your Commission @ 1%</div><div className="text-[#F4CF52] font-mono-alt font-black text-xl">{inrFull(d.commission_amount)}</div></div>
          <CommBadge paid={d.commission_paid} size="lg"/>
        </div>
      </div>
      <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-3">Tracking Status</div>
        <div className="relative pl-6">
          <div className="absolute top-2 bottom-2 left-[11px] w-0.5 bg-slate-800"/>
          {d.timeline.map((s,i)=><TL key={i} step={s}/>)}
        </div>
      </div>
      {d.commission_paid ? (
        <div className="rounded-2xl border-2 border-[#22C55E]/60 p-4 text-center" style={{background:"linear-gradient(140deg,#052e18,#0a4023)",boxShadow:"0 0 24px -4px rgba(34,197,94,0.35)"}}>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4ADE80] font-bold">Commission</div>
          <div className="font-heading text-2xl text-white mt-1">{inrFull(d.commission_amount)} Paid <span className="text-[#4ADE80]">✓</span></div>
          <div className="text-xs text-[#4ADE80]/80 mt-1">Credited {fmtDateTime(d.commission_paid_at)}</div>
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-[#F97316]/50 p-4 text-center" style={{background:"linear-gradient(140deg,#2b1508,#3a1e08)"}}>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F97316] font-bold">Pending Commission</div>
          <div className="font-heading text-2xl text-white mt-1">{inrFull(d.commission_amount)}</div>
          <div className="text-xs text-[#FDB979] mt-1">Will be credited after admin confirmation</div>
        </div>
      )}
    </div>
  );
}
function TL({ step }) {
  const done = step.done, isLast = step.label === "Disbursed";
  const dot = done ? (isLast?"bg-gradient-to-br from-[#D4AF37] to-[#8a6f1c] text-slate-900":"bg-[#22C55E] text-slate-900") : "bg-slate-800 text-slate-600 border border-slate-700";
  const txt = done ? (isLast?"text-[#D4AF37]":"text-emerald-300") : "text-slate-500";
  return (
    <div className="relative pb-4 last:pb-0">
      <div className={`absolute -left-6 top-0 w-6 h-6 rounded-full flex items-center justify-center ${dot}`}>{done ? <CheckCircle2 className="w-3.5 h-3.5"/> : <Clock className="w-3.5 h-3.5"/>}</div>
      <div className={`text-sm font-bold ${txt}`}>{step.label}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{done ? `Completed · ${fmtDateTime(step.at)}` : "Pending"}</div>
    </div>
  );
}

// ---------- Commission tab ----------
function CommissionTab() {
  const [stats, setStats] = useState({});
  const [items, setItems] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/partner/refer/stats"), api.get("/partner/refer/list")])
      .then(([s,l]) => { setStats(s.data); setItems(l.data.items||[]); });
  }, []);
  const paid = items.filter(i => i.commission_paid);
  const pending = items.filter(i => !i.commission_paid);
  return (
    <div className="space-y-3" data-testid="partner-commission-tab">
      <EarningsCard stats={stats}/>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4ADE80] font-bold mb-2 mt-1">Paid ({paid.length})</div>
        {paid.length === 0 ? <div className="text-slate-600 text-xs italic px-1 mb-3">None yet</div> :
          <div className="space-y-2 mb-3">{paid.map(it=><LeadCard key={it.lead_id} it={it}/>)}</div>}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#F97316] font-bold mb-2">Pending ({pending.length})</div>
        {pending.length === 0 ? <div className="text-slate-600 text-xs italic px-1">None</div> :
          <div className="space-y-2">{pending.map(it=><LeadCard key={it.lead_id} it={it}/>)}</div>}
      </div>
    </div>
  );
}

// ---------- Profile tab ----------
function ProfileTab({ profile }) {
  if (!profile) return <div className="text-slate-500 text-center py-8">Loading…</div>;
  const verified = profile.verification_status === "verified";
  return (
    <div className="space-y-3" data-testid="partner-profile-tab">
      <div className="rounded-2xl border border-[#1E2742] bg-[#141A2E] p-4">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8a6f1c] flex items-center justify-center text-slate-900 font-black text-lg">
            {(profile.name||"?").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-heading text-lg">{profile.name}</div>
            <div className="text-[11px] font-mono-alt text-[#D4AF37]">{profile.partner_id || "PENDING VERIFY"}</div>
          </div>
          {verified ?
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 text-[10px] px-2 py-1 font-bold uppercase tracking-widest"><CheckCircle2 className="w-3 h-3"/> Verified</span>
            :
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/50 text-[10px] px-2 py-1 font-bold uppercase tracking-widest">Pending</span>
          }
        </div>
      </div>

      <div className="rounded-2xl border border-[#1E2742] bg-[#141A2E] p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-3 flex items-center gap-1.5"><Store className="w-3.5 h-3.5"/> Office Details</div>
        <div className="space-y-2 text-sm">
          <Field label="Office Name" value={profile.office_name}/>
          <Field label="Office Type" value={profile.office_type}/>
          <Field label="Full Address" value={profile.office_address}/>
          <Field label="District / Branch" value={`${profile.district || "—"} · ${profile.branch_name || "—"}`}/>
        </div>
        {profile.office_photo_b64 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Office Photo</div>
            <img src={profile.office_photo_b64} alt="office" className="w-full max-h-48 object-cover rounded-lg border border-white/10"/>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[#1E2742] bg-[#141A2E] p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-3">Downloads</div>
        <div className="space-y-2">
          <DownloadRow label="Authorized Partner Board" desc="Auto-filled with your name + branch" onClick={()=>downloadBoard(profile)} testId="dl-board" enabled={verified}/>
          <DownloadRow label="Partner Certificate" desc="PDF-style certificate" onClick={()=>downloadCertificate(profile)} testId="dl-cert" enabled={verified}/>
          <DownloadRow label="Visiting Card Template" desc="Print at any Xerox shop" onClick={()=>downloadVCard(profile)} testId="dl-vcard" enabled={verified}/>
        </div>
        {!verified && <div className="mt-2 text-[10px] text-orange-400">Downloads unlock after office verification.</div>}
      </div>
    </div>
  );
}
function Field({ label, value }) {
  return (<div className="flex justify-between gap-3"><div className="text-slate-500 text-[11px] uppercase tracking-widest font-bold shrink-0">{label}</div><div className="text-slate-200 text-right">{value || "—"}</div></div>);
}
function DownloadRow({ label, desc, onClick, testId, enabled }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} disabled={!enabled} className="w-full text-left flex items-center gap-3 p-3 rounded-lg bg-slate-950/60 border border-white/10 hover:border-amber-500/30 disabled:opacity-40 disabled:cursor-not-allowed">
      <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center"><Sparkles className="w-4 h-4"/></div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-bold text-sm">{label}</div>
        <div className="text-[11px] text-slate-500">{desc}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-500"/>
    </button>
  );
}

function downloadBoard(p) {
  // SVG board rendered client-side, downloaded as file.
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="900" height="600" viewBox="0 0 900 600">
  <defs>
    <linearGradient id="g" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stop-color="#0F172A"/><stop offset="100%" stop-color="#1a1305"/></linearGradient>
  </defs>
  <rect width="900" height="600" fill="url(#g)"/>
  <rect x="20" y="20" width="860" height="560" fill="none" stroke="#D4AF37" stroke-width="6" rx="20"/>
  <text x="450" y="120" text-anchor="middle" fill="#D4AF37" font-family="Arial Black,Arial,sans-serif" font-size="42" letter-spacing="8">SHAKTHI LOAN CONSULTANCY</text>
  <text x="450" y="170" text-anchor="middle" fill="#F97316" font-family="Arial,sans-serif" font-size="24" letter-spacing="12">— AUTHORIZED PARTNER —</text>
  <text x="450" y="290" text-anchor="middle" fill="#FFFFFF" font-family="Arial Black,sans-serif" font-size="56">${escapeHtml((p.office_name||"OFFICE").toUpperCase())}</text>
  <text x="450" y="340" text-anchor="middle" fill="#D4AF37" font-family="Arial,sans-serif" font-size="26">${escapeHtml((p.name||"").toUpperCase())} · ${escapeHtml(p.partner_id||"")}</text>
  <text x="450" y="400" text-anchor="middle" fill="#94a3b8" font-family="Arial,sans-serif" font-size="22">${escapeHtml((p.branch_name||"").toUpperCase())} · ${escapeHtml((p.district||"").toUpperCase())}</text>
  <text x="450" y="500" text-anchor="middle" fill="#F97316" font-family="Arial,sans-serif" font-size="22" letter-spacing="6">HOME · PERSONAL · BUSINESS · LAP · VEHICLE · GOLD</text>
  <text x="450" y="550" text-anchor="middle" fill="#22C55E" font-family="Arial Black,sans-serif" font-size="20">✓ VERIFIED OFFICE  ·  CALL: ${escapeHtml(p.phone||"")}</text>
</svg>`;
  downloadBlob(svg, `partner-board-${p.partner_id || p.name}.svg`, "image/svg+xml");
  toast.success("Board template downloaded");
}
function downloadCertificate(p) {
  const now = new Date();
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="850" viewBox="0 0 1200 850">
  <rect width="1200" height="850" fill="#0F172A"/>
  <rect x="30" y="30" width="1140" height="790" fill="none" stroke="#D4AF37" stroke-width="4"/>
  <rect x="46" y="46" width="1108" height="758" fill="none" stroke="#D4AF37" stroke-width="1"/>
  <text x="600" y="140" text-anchor="middle" fill="#D4AF37" font-family="Georgia,serif" font-size="48" font-style="italic">Certificate of Partnership</text>
  <text x="600" y="200" text-anchor="middle" fill="#94a3b8" font-family="Arial,sans-serif" font-size="20" letter-spacing="10">SHAKTHI LOAN CONSULTANCY</text>
  <text x="600" y="330" text-anchor="middle" fill="#FFFFFF" font-family="Arial,sans-serif" font-size="24">This certifies that</text>
  <text x="600" y="400" text-anchor="middle" fill="#F4CF52" font-family="Georgia,serif" font-size="56" font-weight="bold">${escapeHtml(p.name||"")}</text>
  <text x="600" y="450" text-anchor="middle" fill="#94a3b8" font-family="Arial,sans-serif" font-size="20">Partner ID · ${escapeHtml(p.partner_id||"")}</text>
  <text x="600" y="530" text-anchor="middle" fill="#FFFFFF" font-family="Arial,sans-serif" font-size="20">is an <tspan fill="#F97316" font-weight="bold">Authorized Partner</tspan> operating from</text>
  <text x="600" y="580" text-anchor="middle" fill="#FFFFFF" font-family="Arial,sans-serif" font-size="22">${escapeHtml(p.office_name||"")} · ${escapeHtml(p.branch_name||"")} · ${escapeHtml(p.district||"")}</text>
  <text x="600" y="720" text-anchor="middle" fill="#94a3b8" font-family="Arial,sans-serif" font-size="16">Issued ${escapeHtml(now.toLocaleDateString("en-GB"))} · Valid till revoked</text>
</svg>`;
  downloadBlob(svg, `partner-certificate-${p.partner_id || p.name}.svg`, "image/svg+xml");
  toast.success("Certificate downloaded");
}
function downloadVCard(p) {
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1050" height="600" viewBox="0 0 1050 600">
  <rect width="1050" height="600" fill="#0F172A"/>
  <rect x="0" y="0" width="1050" height="12" fill="#D4AF37"/>
  <rect x="0" y="588" width="1050" height="12" fill="#F97316"/>
  <text x="60" y="120" fill="#D4AF37" font-family="Arial,sans-serif" font-size="22" letter-spacing="8">SHAKTHI LOAN CONSULTANCY</text>
  <text x="60" y="160" fill="#F97316" font-family="Arial,sans-serif" font-size="16" letter-spacing="4">AUTHORIZED PARTNER · ${escapeHtml(p.partner_id||"")}</text>
  <text x="60" y="260" fill="#FFFFFF" font-family="Arial Black,sans-serif" font-size="52">${escapeHtml(p.name||"")}</text>
  <text x="60" y="310" fill="#94a3b8" font-family="Arial,sans-serif" font-size="22">${escapeHtml(p.experience_type||"DSA")}  ·  ${escapeHtml(p.branch_name||"")}</text>
  <text x="60" y="420" fill="#D4AF37" font-family="Arial,sans-serif" font-size="20">📞 +91 ${escapeHtml(p.phone||"")}</text>
  <text x="60" y="460" fill="#94a3b8" font-family="Arial,sans-serif" font-size="16">${escapeHtml(p.office_name||"")}</text>
  <text x="60" y="490" fill="#94a3b8" font-family="Arial,sans-serif" font-size="14">${escapeHtml(p.office_address||"")}</text>
  <text x="60" y="540" fill="#22C55E" font-family="Arial,sans-serif" font-size="14">✓ Home · Personal · Business · LAP · Vehicle · Gold</text>
</svg>`;
  downloadBlob(svg, `partner-visiting-card-${p.partner_id || p.name}.svg`, "image/svg+xml");
  toast.success("Visiting card downloaded");
}
function escapeHtml(s) { return String(s||"").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c])); }
function downloadBlob(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// ---------- Add Partner Lead dialog ----------
function AddPartnerLeadDialog({ open, onClose, profile, onDone }) {
  const { districts } = useLocations();
  const [f, setF] = useState({ name:"", phone:"", district: profile?.district || "", branch_id: profile?.branch_id || "", loan_type:"", amount:"", notes:"" });
  const [submitting, setSubmitting] = useState(false);
  const [loans, setLoans] = useState([]);

  useEffect(() => {
    if (open) setF({ name:"", phone:"", district: profile?.district || "", branch_id: profile?.branch_id || "", loan_type:"", amount:"", notes:"" });
  }, [open, profile]);
  useEffect(() => {
    api.get("/loans").then(r => setLoans(r.data || [])).catch(() => setLoans([]));
  }, []);

  const distObj = useMemo(() => districts.find(d => d.name === f.district), [districts, f.district]);
  const { branches } = useBranches(distObj?.id);

  const refLabel = `${profile?.name || "Partner"} - ${profile?.partner_id || "PENDING"} - Branch: ${profile?.branch_name || "—"}`;

  const submit = async () => {
    if (!f.name || f.phone.length !== 10 || !f.district || !f.loan_type || !f.amount) return toast.error("Fill all required fields");
    setSubmitting(true);
    try {
      const { data } = await api.post("/partner/leads", { ...f, amount: parseFloat(f.amount) });
      toast.success(`Lead created: ${data.lead_id} · Commission ₹${(data.commission_amount||0).toLocaleString("en-IN")}`);
      onDone && onDone(); onClose();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmitting(false); }
  };

  const estCommission = useMemo(() => Math.round(parseFloat(f.amount||"0")*0.01), [f.amount]);

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="bg-[#0F172A] border-[#D4AF37]/30 text-white max-h-[92vh] overflow-y-auto max-w-md" data-testid="add-partner-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#D4AF37]"><Gift className="w-5 h-5"/> Add Customer Lead</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <Label className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold">Referred By (Auto)</Label>
            <div data-testid="ref-locked" className="mt-1.5 h-11 rounded-md bg-[#0A0E1E] border border-[#D4AF37]/40 px-3 flex items-center text-[#D4AF37] font-mono-alt text-xs opacity-90">{refLabel}</div>
          </div>
          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Customer Name *</Label>
            <Input data-testid="pl-name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="bg-[#0A0E1E] border-slate-700 h-11 mt-1.5"/>
          </div>
          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Phone (10 digits) *</Label>
            <Input data-testid="pl-phone" inputMode="numeric" maxLength={10} value={f.phone} onChange={e=>setF({...f,phone:e.target.value.replace(/\D/g,"")})} className="bg-[#0A0E1E] border-slate-700 h-11 mt-1.5 font-mono-alt"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">District *</Label>
              <Select value={f.district} onValueChange={v=>setF({...f,district:v,branch_id:""})}>
                <SelectTrigger data-testid="pl-district" className="bg-[#0A0E1E] border-slate-700 h-11 mt-1.5"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#0F172A] text-white border-slate-700 max-h-64">
                  {districts.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Branch</Label>
              <Select value={f.branch_id} onValueChange={v=>setF({...f,branch_id:v})} disabled={!distObj}>
                <SelectTrigger data-testid="pl-branch" className="bg-[#0A0E1E] border-slate-700 h-11 mt-1.5"><SelectValue placeholder={distObj?"Optional":"Pick district"}/></SelectTrigger>
                <SelectContent className="bg-[#0F172A] text-white border-slate-700 max-h-64">
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.branch_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Loan Type *</Label>
            <Select value={f.loan_type} onValueChange={v=>setF({...f,loan_type:v})}>
              <SelectTrigger data-testid="pl-loan" className="bg-[#0A0E1E] border-slate-700 h-11 mt-1.5"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#0F172A] text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                {loans.length === 0
                  ? <div className="p-3 text-sm text-slate-400">No loans configured. Contact admin.</div>
                  : loans.map(l => <SelectItem key={l.id || l.name} value={l.name}><span className="inline-flex items-center gap-2"><LoanIcon loan={l} className="w-4 h-4 text-[#D4AF37]"/>{l.name}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">Loan Amount (₹) *</Label>
            <div className="relative mt-1.5">
              <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
              <Input
                data-testid="pl-amount"
                inputMode="numeric"
                value={f.amount}
                onChange={e=>setF({...f,amount:e.target.value.replace(/\D/g,"")})}
                onBlur={()=>{ const c = clampAmount(f.amount, findLoan(loans, f.loan_type)); if (c.changed) { setF({...f, amount: c.value}); toast.message(c.why); } }}
                className="bg-[#0A0E1E] border-slate-700 pl-9 h-11 font-mono-alt"/>
            </div>
            {f.loan_type && loanRangeLabel(findLoan(loans, f.loan_type)) && (
              <div className="mt-1 text-[11px] text-[#D4AF37]/80 font-mono-alt" data-testid="pl-amount-range">{loanRangeLabel(findLoan(loans, f.loan_type))}</div>
            )}
            {estCommission > 0 && <div className="mt-1.5 text-[11px] text-[#D4AF37] font-mono-alt">Est. commission: ₹{estCommission.toLocaleString("en-IN")} (1%)</div>}
          </div>
          <div className="rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/40 p-3 text-xs text-[#D4AF37]/90">
            <div className="font-bold text-[#D4AF37] mb-1">You will earn 1% commission</div>
            <div>Eg: ₹5,00,000 = <b className="text-[#F4CF52]">₹5,000</b> after disbursal.</div>
          </div>
          <Button data-testid="pl-submit" disabled={submitting} onClick={submit} className="w-full h-12 bg-[#F97316] hover:bg-[#ea6a10] text-white font-bold rounded-full text-sm">
            {submitting ? "Submitting…" : "Submit Lead"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
